import { Dialog } from "@kobalte/core/dialog";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { TbX } from "solid-icons/tb";
import { type Component, createSignal, Show } from "solid-js";
import { useI18n } from "~/i18n/context";

interface ArchiveMemoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  memoryContent: string;
}

export const ArchiveMemoryModal: Component<ArchiveMemoryModalProps> = (props) => {
  const { t } = useI18n();
  const [loading, setLoading] = createSignal(false);
  const [reason, setReason] = createSignal("");
  const [archiveError, setArchiveError] = createSignal<string | null>(null);

  const handleArchive = async () => {
    if (loading()) return;
    setArchiveError(null);
    setLoading(true);
    try {
      await props.onConfirm(reason());
      props.onClose();
      setReason("");
    } catch (e) {
      setArchiveError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const truncatedContent = () => {
    const content = props.memoryContent;
    return content.length > 120 ? `${content.slice(0, 120)}...` : content;
  };

  return (
    <AnimateChildren show={props.isOpen} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) props.onClose();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed inset-0 z-90 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed inset-0 z-90 flex items-center justify-center">
              <Dialog.Content class="relative max-h-[80vh] w-screen max-w-md overflow-y-auto rounded-xl bg-panel p-6 text-primary-text">
                <Dialog.CloseButton as={StButton} class="absolute top-4 right-4">
                  <TbX />
                </Dialog.CloseButton>

                <div class="mb-6">
                  <h3 class="mb-4 text-lg font-medium">{t("memory.archive.modal_title")}</h3>
                  <p class="mb-3 text-sm text-secondary-text">{t("memory.archive.modal_description")}</p>
                  <div class="mb-3 rounded-lg border border-muted-border bg-canvas p-3">
                    <p class="text-sm text-muted-text italic">{truncatedContent()}</p>
                  </div>
                  <textarea
                    value={reason()}
                    onInput={(e) => setReason(e.currentTarget.value)}
                    placeholder={t("memory.archive.reason_placeholder")}
                    class="h-16 w-full resize-none rounded-lg border border-muted-border bg-canvas p-3 text-sm text-primary-text focus:border-brand focus:ring-2 focus:ring-brand focus:outline-none"
                  />
                </div>

                <Show when={archiveError()}>
                  <div class="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/20">
                    <p class="text-sm text-red-700 dark:text-red-300">{archiveError()}</p>
                  </div>
                </Show>

                <div class="flex items-center justify-end gap-3">
                  <StButton onClick={props.onClose} class="px-4 py-2 text-secondary-text hover:text-primary-text">
                    {t("common.cancel")}
                  </StButton>
                  <StButton
                    onClick={() => void handleArchive()}
                    loading={loading()}
                    disabled={loading()}
                    class="bg-amber-600 px-4 py-2 text-white hover:bg-amber-700"
                  >
                    {t("memory.archive.button")}
                  </StButton>
                </div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
